// ============================================================================
// hud.js — the persistent overlay: HP, Titan gauge (flashes when full), combo
// counter (decays), kill count, morale tug-of-war, the warlord boss bar, the
// objective ticker, and world-space floating damage numbers projected to screen.
// Pure DOM over the canvas. Text is only written when it changes.
// ============================================================================

import * as THREE from 'three';
import { PLAYER_HP_MAX, TITAN_MAX, HIT_COUNTER_DECAY, MORALE_MAX } from '../core/constants.js';
import { EV } from '../core/events.js';

const CSS = `
#hud .panel{position:fixed;font-family:'Trebuchet MS',system-ui,sans-serif;}
#hud .bar{background:rgba(0,0,0,.45);border:1px solid rgba(255,106,26,.35);overflow:hidden;}
#hud .fill{height:100%;transition:width .12s linear;}
#hp{left:26px;bottom:30px;width:300px;}
#hp .lbl{color:#d7dbe0;font-size:13px;letter-spacing:.18em;margin-bottom:4px;font-weight:700;}
#hp .bar{height:16px;}
#hp .fill{background:linear-gradient(90deg,#8a1414,#e23b1a);}
#titan{left:26px;bottom:6px;width:300px;}
#titan .bar{height:10px;}
#titan .fill{background:linear-gradient(90deg,#3a2a6a,#a06aff,#ffb257);}
#titan.ready .bar{border-color:#ffb257;box-shadow:0 0 14px rgba(255,178,87,.8);}
#titan .rdy{color:#ffb257;font-size:11px;letter-spacing:.2em;margin-top:3px;height:14px;font-weight:700;}
#combo{right:34px;top:74px;text-align:right;color:#fff;}
#combo .n{font-size:56px;font-weight:900;line-height:.9;color:#ffb257;text-shadow:0 0 18px rgba(255,106,26,.7),0 3px 6px #000;}
#combo .t{font-size:14px;letter-spacing:.3em;color:#d7dbe0;}
#kills{right:34px;top:24px;text-align:right;color:#b9b2a8;}
#kills .n{font-size:26px;font-weight:800;color:#d7dbe0;}
#kills .t{font-size:11px;letter-spacing:.28em;}
#morale{left:50%;top:14px;transform:translateX(-50%);width:340px;}
#morale .t{color:#8c857c;font-size:10px;letter-spacing:.28em;text-align:center;margin-bottom:3px;}
#morale .track{height:9px;display:flex;border:1px solid rgba(0,0,0,.6);}
#morale .p{background:linear-gradient(90deg,#39415a,#aeb6c4);}
#morale .m{background:linear-gradient(90deg,#c23a12,#5a1408);}
#boss{left:50%;top:44px;transform:translateX(-50%);width:520px;text-align:center;display:none;}
#boss .nm{color:#ff7a2a;font-size:18px;font-weight:900;letter-spacing:.08em;text-shadow:0 2px 6px #000;}
#boss .ti{color:#b9b2a8;font-size:11px;letter-spacing:.14em;margin:1px 0 4px;font-style:italic;}
#boss .bar{height:12px;border-color:rgba(255,106,26,.6);}
#boss .fill{background:linear-gradient(90deg,#6b1414,#ff5a12);}
#objective{left:50%;top:auto;bottom:26px;transform:translateX(-50%);color:#e8c9a0;font-size:14px;letter-spacing:.06em;max-width:520px;text-align:center;text-shadow:0 2px 5px #000;opacity:.9;}
#objective .tag{color:#ff7a2a;font-weight:800;letter-spacing:.2em;margin-right:8px;}
#dmg-layer{position:fixed;inset:0;pointer-events:none;overflow:hidden;}
.dmg{position:absolute;font-weight:900;font-family:'Trebuchet MS',sans-serif;transform:translate(-50%,-50%);will-change:transform,opacity;text-shadow:0 2px 4px #000;}
`;

export class HUD {
  constructor(hudRoot, world) {
    this.world = world;
    const style = document.createElement('style');
    style.textContent = CSS;
    document.head.appendChild(style);

    hudRoot.insertAdjacentHTML('beforeend', `
      <div id="hp" class="panel"><div class="lbl">COMMANDER BENJEN DAGUTH</div><div class="bar"><div class="fill" style="width:100%"></div></div></div>
      <div id="titan" class="panel"><div class="bar"><div class="fill" style="width:0%"></div></div><div class="rdy"></div></div>
      <div id="kills" class="panel"><div class="n">0</div><div class="t">MAGMAR SLAIN</div></div>
      <div id="combo" class="panel"><div class="n"></div><div class="t"></div></div>
      <div id="morale" class="panel"><div class="t">THE TIDE OF BATTLE</div><div class="track"><div class="p" style="width:50%"></div><div class="m" style="width:50%"></div></div></div>
      <div id="boss" class="panel"><div class="nm"></div><div class="ti"></div><div class="bar"><div class="fill" style="width:100%"></div></div></div>
      <div id="objective" class="panel"><span class="tag">ORDERS</span><span class="txt">Break the Magmar host. Slay the Dynast in his citadel.</span></div>
      <div id="dmg-layer"></div>
      <div id="lowhp"></div>
    `);
    const lowStyle = document.createElement('style');
    lowStyle.textContent = `#lowhp{position:fixed;inset:0;pointer-events:none;z-index:9;opacity:0;
      background:radial-gradient(ellipse at center, rgba(0,0,0,0) 52%, rgba(150,12,12,0.6) 100%);transition:opacity .12s;}`;
    document.head.appendChild(lowStyle);
    this.lowhp = hudRoot.querySelector('#lowhp');

    this.hpFill = hudRoot.querySelector('#hp .fill');
    this.titan = hudRoot.querySelector('#titan');
    this.titanFill = hudRoot.querySelector('#titan .fill');
    this.titanRdy = hudRoot.querySelector('#titan .rdy');
    this.killN = hudRoot.querySelector('#kills .n');
    this.comboN = hudRoot.querySelector('#combo .n');
    this.comboT = hudRoot.querySelector('#combo .t');
    this.moraleP = hudRoot.querySelector('#morale .p');
    this.moraleM = hudRoot.querySelector('#morale .m');
    this.boss = hudRoot.querySelector('#boss');
    this.bossNm = hudRoot.querySelector('#boss .nm');
    this.bossTi = hudRoot.querySelector('#boss .ti');
    this.bossFill = hudRoot.querySelector('#boss .fill');
    this.objTxt = hudRoot.querySelector('#objective .txt');
    this.dmgLayer = hudRoot.querySelector('#dmg-layer');

    // damage-number DOM pool
    this.dmgPool = [];
    for (let i = 0; i < 128; i++) {
      const el = document.createElement('div');
      el.className = 'dmg'; el.style.display = 'none';
      this.dmgLayer.appendChild(el);
      this.dmgPool.push(el);
    }

    world.events.on(EV.OBJECTIVE, ({ text }) => { this.objTxt.textContent = text; });

    this._last = { hp: -1, titan: -1, ready: null, kills: -1, combo: -1, morale: -1, boss: null, bossHp: -1 };
    this._v = new THREE.Vector3();
  }

  update(world, camera, dt) {
    const p = world.player;

    const hpPct = Math.max(0, p.hp / PLAYER_HP_MAX) * 100;
    if (hpPct !== this._last.hp) { this.hpFill.style.width = hpPct + '%'; this._last.hp = hpPct; }

    // low-HP danger vignette (pulses harder the closer to death)
    const frac = p.hp / p.maxHp;
    if (frac < 0.35 && p.state !== 'dead') {
      const sev = 1 - frac / 0.35;
      this.lowhp.style.opacity = (0.35 + 0.5 * sev) * (0.7 + 0.3 * Math.sin(world.simTime * (6 + sev * 6)));
    } else if (this.lowhp.style.opacity !== '0') {
      this.lowhp.style.opacity = '0';
    }

    const tPct = (p.titan / TITAN_MAX) * 100;
    if (Math.abs(tPct - this._last.titan) > 0.5) { this.titanFill.style.width = tPct + '%'; this._last.titan = tPct; }
    if (p.titanReady !== this._last.ready) {
      this.titan.classList.toggle('ready', p.titanReady);
      this.titanRdy.textContent = p.titanReady ? '◆ TITANFALL READY — HOLD [Q]' : '';
      this._last.ready = p.titanReady;
    }
    if (p.titanReady) {
      // pulse
      this.titan.style.opacity = 0.75 + 0.25 * Math.sin(world.simTime * 8);
    } else if (this.titan.style.opacity !== '1') this.titan.style.opacity = 1;

    if (world.killCount !== this._last.kills) { this.killN.textContent = world.killCount; this._last.kills = world.killCount; }

    // combo (fades via comboTimer)
    if (world.combo >= 2) {
      const fade = Math.max(0, 1 - world.comboTimer / HIT_COUNTER_DECAY);
      this.comboN.textContent = world.combo;
      this.comboT.textContent = 'HITS';
      this.comboN.style.opacity = 0.3 + 0.7 * fade;
      this.comboT.style.opacity = 0.3 + 0.7 * fade;
      const s = 1 + Math.min(0.5, world.combo * 0.004);
      this.comboN.style.transform = `scale(${s})`;
    } else if (this._last.combo !== 0) {
      this.comboN.textContent = ''; this.comboT.textContent = '';
    }
    this._last.combo = world.combo;

    const mor = (world.morale / MORALE_MAX) * 100;
    if (Math.abs(mor - this._last.morale) > 0.4) {
      this.moraleP.style.width = mor + '%';
      this.moraleM.style.width = (100 - mor) + '%';
      this._last.morale = mor;
    }

    // boss bar
    const w = world.engagedWarlord;
    if (w && w._active && w.state !== 7) {
      if (this._last.boss !== w) {
        this.boss.style.display = 'block';
        this.bossNm.textContent = w.warlord ? w.warlord.name : 'Magmar Warlord';
        this.bossTi.textContent = w.warlord ? w.warlord.title : '';
        this._last.boss = w;
      }
      const bhp = Math.max(0, w.hp / w.maxHp) * 100;
      if (Math.abs(bhp - this._last.bossHp) > 0.3) { this.bossFill.style.width = bhp + '%'; this._last.bossHp = bhp; }
    } else if (this._last.boss !== null) {
      this.boss.style.display = 'none'; this._last.boss = null; this._last.bossHp = -1;
    }

    this._updateDamageNumbers(world, camera, dt);
  }

  _updateDamageNumbers(world, camera, dt) {
    const arr = world.dmgnums.active;
    const w = window.innerWidth, h = window.innerHeight;
    let idx = 0;
    for (let i = arr.length - 1; i >= 0; i--) {
      const d = arr[i];
      d.life -= dt; d.y += d.vy * dt; d.vy -= 3 * dt;
      if (d.life <= 0) { world.dmgnums.despawn(d); continue; }
      if (idx >= this.dmgPool.length) continue;
      this._v.set(d.x, d.y, d.z).project(camera);
      const el = this.dmgPool[idx++];
      if (this._v.z > 1) { el.style.display = 'none'; continue; }
      const sx = (this._v.x * 0.5 + 0.5) * w;
      const sy = (-this._v.y * 0.5 + 0.5) * h;
      const t = d.life / d.maxLife;
      el.style.display = 'block';
      el.style.left = sx + 'px'; el.style.top = sy + 'px';
      el.style.opacity = Math.min(1, t * 1.6);
      let text, color, size;
      if (d.kind === 4) { text = '+' + d.value; color = '#7dff8a'; size = 20; }
      else if (d.kind === 3) { text = 'GUARD BROKEN'; color = '#ffd05a'; size = 22; }
      else if (d.kind === 2) { text = 'GUARD!'; color = '#9ad0ff'; size = 18; }
      else if (d.kind === 1) { text = d.value; color = '#ff9a3a'; size = 30; }
      else { text = d.value; color = '#f4e8dc'; size = 20; }
      el.textContent = text;
      el.style.color = color;
      el.style.fontSize = (size * (0.7 + t * 0.5)) + 'px';
    }
    // hide leftover pooled elements
    for (; idx < this.dmgPool.length; idx++) {
      if (this.dmgPool[idx].style.display !== 'none') this.dmgPool[idx].style.display = 'none';
    }
  }
}
